import { ReactNode, useEffect, useState } from 'react';

import { Box } from '@material-ui/core';

import upperFirst from 'lodash/upperFirst';

import { ConfirmDialog, useDialog } from '@spotify/confidence-core-react';
import { PermissionUtils } from '@spotify/plugin-confidence-permissions-react';

import { useFormatWorkflowDisplayName } from '../../hooks/useFormatWorkflowDisplayName';
import { WorkflowInstance } from '../../hooks/useGetWorkflowInstance';
import { useTransitionWorkflowInstance } from '../../hooks/useTransitionWorkflowInstance';
import { DeleteWorkflowInstanceDialog } from '../DeleteWorkflowInstanceDialog';
import { WorkflowPermission } from '../WorkflowPermission';
import type { RecommendedAction } from '../recommendations';
import { ActionButton } from './ActionButton';

type ActionButtonsListProps = {
  workflowInstance: WorkflowInstance;
  workflowId: string;
  disabledReason?: (action: string) => string | null;
  /* Rendered below the confirmation text in the transition dialog */
  confirmDialogContent?: (action: string) => ReactNode;
  possibleTransitions?: string[];
  onTransitionCompleted?: (state: string) => void;
  children?: ReactNode;
  prepend?: boolean;
  recommendedAction?: RecommendedAction;
};

export const formatAction = (transitions: string) =>
  transitions.replace(/([A-Z]+)/g, ' $1').toLowerCase();

export const ActionButtonsList = ({
  workflowInstance,
  workflowId,
  disabledReason = () => null,
  confirmDialogContent,
  possibleTransitions: possibleTransitionsOverride,
  onTransitionCompleted,
  children,
  prepend = false,
  recommendedAction,
}: ActionButtonsListProps) => {
  const { openDialog } = useDialog();
  const formatWorkflowDisplayName = useFormatWorkflowDisplayName();
  const workflowLabel = formatWorkflowDisplayName(workflowId);

  // Track the previous state to detect when transitions complete
  const [previousState, setPreviousState] = useState(workflowInstance.state);

  // Update previous state when current state changes
  useEffect(() => {
    if (previousState !== workflowInstance.state) {
      onTransitionCompleted?.(workflowInstance.state);
    }

    setPreviousState(workflowInstance.state);
  }, [workflowInstance.state, previousState, onTransitionCompleted]);

  const { loading, performTransition } = useTransitionWorkflowInstance({
    workflowInstanceName: workflowInstance.name,
  });

  const [confirmingAction, setConfirmingAction] = useState<string | null>(null);
  const confirmExtraContent =
    confirmingAction && confirmDialogContent?.(confirmingAction);

  const confirmDelete = () => {
    return openDialog({
      content: (
        <DeleteWorkflowInstanceDialog
          instance={workflowInstance}
          workflowId={workflowId}
         data-track-source-anchor="b067d543f8;action-buttons-list-delete-workflow-instance"/>
      ),
    });
  };

  const possibleTransitions =
    possibleTransitionsOverride ?? workflowInstance.possibleTransitions;

  const possibleTransitionsSorted = [
    ...possibleTransitions.filter(action => action === 'launch'),
    ...possibleTransitions.filter(action => action !== 'launch'),
  ];

  const state = workflowInstance.state.toLowerCase();
  const isLive = state === 'live';
  const showDeleteButton =
    state === 'draft' ||
    state === 'active' ||
    isLive ||
    possibleTransitions.length === 0;
  const canDelete = PermissionUtils.hasRelation(workflowInstance, 'can_edit');

  let deleteDisabledReason: string | null = null;
  if (isLive) {
    deleteDisabledReason = `Cannot delete a live ${workflowLabel}`;
  } else if (!canDelete) {
    deleteDisabledReason = `You don't have permission to delete this ${workflowLabel}`;
  }

  const getPermissionMessage = (
    missingPermissions?: Array<{
      isAllowed: boolean;
      relation: string;
      resource: string;
    }>,
  ) => {
    if (!missingPermissions?.length) return null;

    return missingPermissions
      .map(check => {
        const resource = check.resource || 'resource';
        const relation = check.relation || '';
        return `Missing ${relation} permission for ${resource}`;
      })
      .join(', ');
  };

  return (
    <Box display="flex" gridGap={8} flexWrap="wrap">
      {prepend && children}
      {possibleTransitionsSorted.map(action => (
        <WorkflowPermission key={action} type="action" name={action}>
          {({ allowed, missingPermissions }) => (
            <ActionButton
              action={action}
              primary={action === 'launch'}
              recommended={action === recommendedAction}
              disabled={!!disabledReason(action) || loading || !allowed}
              disabledReason={
                disabledReason(action) ||
                (!allowed ? getPermissionMessage(missingPermissions) : null)
              }
              onClick={() => setConfirmingAction(action)}
              workflowInstance={workflowInstance}
             data-track-source-anchor="c4fdfdfd76;action-buttons-list-action-button"/>
          )}
        </WorkflowPermission>
      ))}
      {!prepend && children}
      {showDeleteButton && (
        <ActionButton
          action="delete"
          disabled={loading || isLive || !canDelete}
          disabledReason={deleteDisabledReason}
          onClick={() => confirmDelete()}
          workflowInstance={workflowInstance}
         data-track-source-anchor="dd27828606;action-buttons-list-action-button-2"/>
      )}
      {confirmingAction && (
        <ConfirmDialog
          onConfirm={() => performTransition(confirmingAction)}
          onClose={() => setConfirmingAction(null)}
          title={upperFirst(formatAction(confirmingAction))}
         data-track-source-anchor="0d80b9fa61;action-buttons-list-confirm-dialog">
          This {workflowLabel} will {formatAction(confirmingAction)}. The action
          can not be undone. Do you wish to proceed?
          {confirmExtraContent}
        </ConfirmDialog>
      )}
    </Box>
  );
};
